import React from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { Theme } from '../Theme';

type CountryDialCodeOption = {
    isoCode: CountryCode;
    label: string;
    dialCode: string;
    flagEmoji: string;
};

type PhoneNumberInputProps = {
    value?: string;
    onChange: (phoneNumber: string) => void;
    disabled?: boolean;
    required?: boolean;
    defaultCountryCode?: CountryCode;
    ariaLabel?: string;
    containerStyle?: React.CSSProperties;
    selectStyle?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
};

const REGION_DISPLAY_NAMES = (() => {
    // Use browser-localized region names where available, with ISO codes as a safe fallback.
    const displayNamesConstructor = (Intl as typeof Intl & {
        DisplayNames?: new (locales: string[], options: { type: 'region' }) => { of: (code: string) => string | undefined };
    }).DisplayNames;
    return displayNamesConstructor ? new displayNamesConstructor(['en'], { type: 'region' }) : null;
})();
const PINNED_COUNTRY_CODES: Array<CountryCode> = ['GB', 'GG', 'IM', 'JE', 'US'];
const PINNED_COUNTRY_ORDER = new Map(PINNED_COUNTRY_CODES.map((isoCode, index) => [isoCode, index]));

function getCountryDisplayName(isoCode: CountryCode): string {
    return REGION_DISPLAY_NAMES?.of(isoCode) || isoCode;
}

function getCountryFlagEmoji(isoCode: CountryCode): string {
    // Convert two-letter region codes into regional indicator flag emoji.
    return isoCode
        .toUpperCase()
        .replace(/./g, (character) =>
            String.fromCodePoint(127397 + character.charCodeAt(0))
        );
}

const COUNTRY_DIAL_CODE_OPTIONS: Array<CountryDialCodeOption> = getCountries()
    .map((isoCode) => ({
        isoCode,
        label: getCountryDisplayName(isoCode),
        dialCode: `+${getCountryCallingCode(isoCode)}`,
        flagEmoji: getCountryFlagEmoji(isoCode),
    }))
    .sort((leftOption, rightOption) => {
        // Keep the common UK/US operating countries at the top, then show all others alphabetically.
        const leftPinnedOrder = PINNED_COUNTRY_ORDER.get(leftOption.isoCode);
        const rightPinnedOrder = PINNED_COUNTRY_ORDER.get(rightOption.isoCode);
        if (leftPinnedOrder != null || rightPinnedOrder != null) {
            return (leftPinnedOrder ?? Number.MAX_SAFE_INTEGER) - (rightPinnedOrder ?? Number.MAX_SAFE_INTEGER);
        }

        return leftOption.label.localeCompare(rightOption.label);
    });

const DIAL_CODE_MATCH_OPTIONS = [...COUNTRY_DIAL_CODE_OPTIONS]
    .sort((leftOption, rightOption) => rightOption.dialCode.length - leftOption.dialCode.length);

function getCountryOption(isoCode?: CountryCode): CountryDialCodeOption {
    // Default to GB because existing imports and historic phone normalisation already used it.
    return COUNTRY_DIAL_CODE_OPTIONS.find((option) => option.isoCode === isoCode) ||
        COUNTRY_DIAL_CODE_OPTIONS[0];
}

function getCountryOptionForValue(value?: string, defaultCountryCode: CountryCode = 'GB'): CountryDialCodeOption {
    // Prefer libphonenumber's parsed country, then fall back to a dial-code prefix match.
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) {
        return getCountryOption(defaultCountryCode);
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(trimmedValue);
    if (parsedPhoneNumber?.country) {
        return getCountryOption(parsedPhoneNumber.country);
    }

    const normalizedDialValue = trimmedValue.startsWith('+') ?
        `+${trimmedValue.replace(/[^\d]/g, '')}` :
        trimmedValue;
    const matchedDialCode = DIAL_CODE_MATCH_OPTIONS.find((option) =>
        normalizedDialValue.startsWith(option.dialCode)
    );

    return matchedDialCode || getCountryOption(defaultCountryCode);
}

function getLocalPhoneNumberForDisplay(value: string | undefined, countryOption: CountryDialCodeOption): string {
    // Display the editable part without the international dial code already represented by the selector.
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) {
        return '';
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(trimmedValue);
    if (parsedPhoneNumber?.country === countryOption.isoCode) {
        return parsedPhoneNumber.formatNational();
    }

    const normalizedDialValue = trimmedValue.startsWith('+') ?
        `+${trimmedValue.replace(/[^\d]/g, '')}` :
        trimmedValue;
    if (normalizedDialValue.startsWith(countryOption.dialCode)) {
        return normalizedDialValue.slice(countryOption.dialCode.length);
    }

    return trimmedValue;
}

function formatPhoneNumberForStorage(localPhoneNumber: string, countryOption: CountryDialCodeOption): string {
    // Store phone numbers with an international prefix so downstream SMS integrations get E.164-like values.
    const trimmedPhoneNumber = localPhoneNumber.trim();
    if (!trimmedPhoneNumber) {
        return '';
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, countryOption.isoCode);
    if (parsedPhoneNumber) {
        return parsedPhoneNumber.format('E.164');
    }

    const digitsOnly = trimmedPhoneNumber.replace(/[^\d]/g, '');
    return digitsOnly ? `${countryOption.dialCode}${digitsOnly}` : '';
}

export function formatPhoneNumberForClientStorage(phoneNumber?: string, defaultCountryCode: CountryCode = 'GB'): string {
    // Normalise save-time values too, covering untouched legacy numbers that do not yet include a dial code.
    const trimmedPhoneNumber = (phoneNumber || '').trim();
    if (!trimmedPhoneNumber) {
        return '';
    }

    const parsedPhoneNumber = trimmedPhoneNumber.startsWith('+') ?
        parsePhoneNumberFromString(trimmedPhoneNumber) :
        parsePhoneNumberFromString(trimmedPhoneNumber, defaultCountryCode);
    if (parsedPhoneNumber) {
        return parsedPhoneNumber.format('E.164');
    }

    const countryOption = getCountryOption(defaultCountryCode);
    const digitsOnly = trimmedPhoneNumber.replace(/[^\d]/g, '');
    return digitsOnly ? `${countryOption.dialCode}${digitsOnly}` : trimmedPhoneNumber;
}

function getSplitPhoneNumber(value?: string, defaultCountryCode: CountryCode = 'GB') {
    // Keep the selector and local input in sync when an existing stored value is loaded.
    const countryOption = getCountryOptionForValue(value, defaultCountryCode);
    return {
        countryOption,
        localPhoneNumber: getLocalPhoneNumberForDisplay(value, countryOption),
    };
}

export function PhoneNumberInput(props: PhoneNumberInputProps) {
    const {
        value,
        onChange,
        disabled,
        required,
        defaultCountryCode = 'GB',
        ariaLabel = 'Phone number',
        containerStyle,
        selectStyle,
        inputStyle,
    } = props;
    const initialPhoneNumber = React.useMemo(
        () => getSplitPhoneNumber(value, defaultCountryCode),
        [defaultCountryCode, value]
    );
    const [selectedCountryCode, setSelectedCountryCode] = React.useState<CountryCode>(initialPhoneNumber.countryOption.isoCode);
    const [localPhoneNumber, setLocalPhoneNumber] = React.useState(initialPhoneNumber.localPhoneNumber);
    const lastEmittedValueRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if ((value || '') === lastEmittedValueRef.current) {
            return;
        }

        const nextPhoneNumber = getSplitPhoneNumber(value, defaultCountryCode);
        setSelectedCountryCode(nextPhoneNumber.countryOption.isoCode);
        setLocalPhoneNumber(nextPhoneNumber.localPhoneNumber);
    }, [defaultCountryCode, value]);

    const selectedCountryOption = getCountryOption(selectedCountryCode);
    const emitPhoneNumberChange = React.useCallback((nextLocalPhoneNumber: string, nextCountryOption: CountryDialCodeOption) => {
        // Emit the storage value while preserving the user's local-number typing in this component.
        const nextPhoneNumber = formatPhoneNumberForStorage(nextLocalPhoneNumber, nextCountryOption);
        lastEmittedValueRef.current = nextPhoneNumber;
        onChange(nextPhoneNumber);
    }, [onChange]);

    return (
        <div style={{ ...styles.container, ...containerStyle }}>
            <select
                value={selectedCountryCode}
                onChange={(event) => {
                    const nextCountryOption = getCountryOption(event.currentTarget.value as CountryCode);
                    setSelectedCountryCode(nextCountryOption.isoCode);
                    if (localPhoneNumber.trim()) {
                        emitPhoneNumberChange(localPhoneNumber, nextCountryOption);
                    }
                }}
                style={{ ...styles.countrySelect, ...selectStyle }}
                disabled={disabled}
                aria-label='Country dial code'
            >
                {COUNTRY_DIAL_CODE_OPTIONS.map((option) => (
                    <option key={option.isoCode} value={option.isoCode}>
                        {option.flagEmoji} {option.label} {option.dialCode}
                    </option>
                ))}
            </select>
            <input
                type='tel'
                value={localPhoneNumber}
                onChange={(event) => {
                    const nextLocalPhoneNumber = event.currentTarget.value;
                    setLocalPhoneNumber(nextLocalPhoneNumber);
                    emitPhoneNumberChange(nextLocalPhoneNumber, selectedCountryOption);
                }}
                style={{ ...styles.phoneInput, ...inputStyle }}
                disabled={disabled}
                required={required}
                aria-label={ariaLabel}
            />
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        minWidth: '220px',
    },
    countrySelect: {
        ...Theme.Fonts.body,
        width: '190px',
        minWidth: '150px',
        marginLeft: 0,
        padding: '10px 12px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box',
    },
    phoneInput: {
        ...Theme.Fonts.body,
        flex: 1,
        minWidth: '150px',
        marginLeft: 0,
        padding: '10px 14px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box',
    },
};
